import axios from "axios";
import { jwtDecode } from "jwt-decode";

export const apiClient = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_API_URL,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

const isPublicClientPortal = () => {
  if (typeof window === "undefined") return false;
  return (
    window.location.pathname.startsWith("/projetos/gestao/token/") ||
    window.location.pathname.startsWith("/questionarios-satisfacao/") ||
    window.location.pathname.startsWith("/inqueritos-idi/")
  );
};

const emitClientPortalInvalid = (detail) => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent("client-portal-token-invalid", { detail: detail || "" }),
  );
};

let isRefreshing = false;
let failedRequestsQueue = [];

// Function to check and refresh token if needed
async function checkAndRefreshToken() {
  let token = localStorage.getItem("access_token");
  const refresh_token = localStorage.getItem("refresh_token");
  const stayOnPage = isPublicClientPortal();

  if (!refresh_token) {
    if (!stayOnPage) {
      console.warn("No tokens available, logging out.");
    }
    logout({ redirectToLogin: !stayOnPage });
    return null;
  }

  if (!token) {
    return await refreshToken();
  }

  try {
    const decoded = jwtDecode(token);
    const now = Math.floor(Date.now() / 1000); // Convert to seconds

    // Ensure token has an expiration field
    if (!decoded.exp) {
      logout({ redirectToLogin: !stayOnPage });
      return null;
    }

    // Refresh if the token expires in less than 30 seconds
    if (decoded.exp < now + 30) {
      return await refreshToken();
    }

    return token;
  } catch (error) {
    console.error("Failed to decode token:", error);
    logout({ redirectToLogin: !stayOnPage });
    return null;
  }
}

// Function to refresh token
async function refreshToken() {
  if (isRefreshing) {
    return new Promise((resolve) => {
      failedRequestsQueue.push(resolve);
    });
  }

  isRefreshing = true;
  const currentRefreshToken = localStorage.getItem("refresh_token");

  try {
    const response = await axios.post(
      `${process.env.REACT_APP_BACKEND_API_URL}/token/refresh/`,
      { refresh: currentRefreshToken },
    );

    if (response.status === 200) {
      const newAccessToken = response.data.access;
      const newRefreshToken = response.data.refresh || currentRefreshToken;

      if (!newAccessToken) {
        throw new Error("No access token returned by refresh endpoint.");
      }

      localStorage.setItem("access_token", newAccessToken);
      if (newRefreshToken) {
        localStorage.setItem("refresh_token", newRefreshToken);
      }

      failedRequestsQueue.forEach((cb) => cb(newAccessToken));
      failedRequestsQueue = [];

      return newAccessToken;
    }
  } catch (error) {
    console.error("Token refresh failed:", error);
    logout({ redirectToLogin: !isPublicClientPortal() });
    return null;
  } finally {
    isRefreshing = false;
  }
}

// Function to log out
function logout({ redirectToLogin = true } = {}) {
  const redirectPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const loginUrl =
    redirectPath && redirectPath !== "/login"
      ? `/login?redirect=${encodeURIComponent(redirectPath)}`
      : "/login";

  localStorage.clear();
  if (redirectToLogin && window.location.pathname !== "/login") {
    window.location.href = loginUrl;
  }
}

// Request interceptor: Check token before making a request
apiClient.interceptors.request.use(
  async (config) => {
    if (
      config.url === "/token/" ||
      config.url === "/token/refresh/" ||
      config.url.startsWith("/password-reset/")
    ) {
      return config;
    }
    try {
      const token = await checkAndRefreshToken();
      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
      return config;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  (error) => Promise.reject(error),
);

// Response interceptor: Handle failed requests
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Ignore requests that were canceled
    if (axios.isCancel(error)) {
      console.warn("Request was canceled:", error.message);
      return Promise.reject(error);
    }

    // Handle 400 errors separately
    if (error.response?.status === 400) {
      console.error("400 Bad Request:", error.response.data);
      return Promise.reject(error);
    }

    // Handle 401 errors (Unauthorized)
    const isClientPortalRequest =
      typeof originalRequest?.url === "string" &&
      originalRequest.url.startsWith("/projects/management/");

    if (isClientPortalRequest && error.response?.status === 403) {
      emitClientPortalInvalid("O link utilizado já não está disponível.");
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const newToken = await refreshToken();

      if (newToken) {
        originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      }
    }

    return Promise.reject(error);
  },
);
